import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionSummary, AccordionDetails, Typography, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FolderOpenOutlinedIcon from '@material-ui/icons/FolderOpenOutlined';
import ProjectEditor from './ProjectEditor';

const useStyles = makeStyles((theme) => ({
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    accordion: {
        border: '1px solid #e1e7ee',
        borderRadius: '8px !important',
        boxShadow: 'none',
        '&:before': {
            display: 'none',
        },
        '&.Mui-expanded': {
            margin: 0,
            borderColor: theme.palette.primary.main,
        },
    },
    summary: {
        minHeight: 64,
        padding: theme.spacing(0, 2),
        scrollMarginTop: theme.spacing(12),
        '&.Mui-expanded': {
            minHeight: 64,
            borderBottom: '1px solid #e7ebf0',
        },
        '& .MuiAccordionSummary-content': {
            alignItems: 'center',
            gap: theme.spacing(1.5),
            minWidth: 0,
            margin: '12px 0',
        },
    },
    icon: {
        width: 36,
        height: 36,
        flexShrink: 0,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#eef4fa',
        color: theme.palette.primary.main,
    },
    heading: {
        flex: 1,
        minWidth: 0,
    },
    title: {
        color: '#1f2937',
        fontSize: '0.9375rem',
        fontWeight: 700,
        lineHeight: 1.35,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    meta: {
        color: '#647184',
        fontSize: '0.75rem',
        lineHeight: 1.45,
        marginTop: 2,
    },
    badge: {
        flexShrink: 0,
        padding: '2px 10px',
        borderRadius: 12,
        fontSize: '0.6875rem',
        fontWeight: 700,
        backgroundColor: '#eef4fa',
        color: '#345474',
    },
    badgeComplete: {
        backgroundColor: '#e7f5ec',
        color: '#287a4b',
    },
    badgeNew: {
        backgroundColor: '#fff4e0',
        color: '#9a6700',
    },
    details: {
        display: 'block',
        padding: theme.spacing(2),
    },
    actionsRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: '1px solid var(--dm-border, #e7ebf0)',
    },
    saveBtn: {
        minWidth: 132,
    },
}));

const ProjectAccordionList = ({
    items,
    section,
    expandedKey,
    onExpand,
    editorRefs,
    onDiscard,
    customerId,
    customerList,
    customerAdminList,
    projectAdminList,
    onProjectAdminCreated,
    programmeOptions,
    onSave,
    savingKey,
}) => {
    const classes = useStyles();

    return (
        <div className={classes.list}>
            {items.map((item) => {
                const isOpen = expandedKey === item.key;
                const badgeClass = item.isNew
                    ? classes.badgeNew
                    : item.status === 'Complete' ? classes.badgeComplete : '';
                return (
                    <Accordion
                        key={item.key}
                        expanded={isOpen}
                        onChange={(evt, open) => onExpand(open ? item.key : null)}
                        className={classes.accordion}
                        TransitionProps={{ unmountOnExit: false }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            className={classes.summary}
                            aria-controls={`${item.key}-content`}
                            id={`${item.key}-header`}
                        >
                            <span className={classes.icon}>
                                <FolderOpenOutlinedIcon fontSize="small" />
                            </span>
                            <div className={classes.heading}>
                                <Typography className={classes.title}>{item.title}</Typography>
                                {item.meta && <Typography className={classes.meta}>{item.meta}</Typography>}
                            </div>
                            {item.programmeLabel && (
                                <span className={classes.badge}>{item.programmeLabel}</span>
                            )}
                            <span className={`${classes.badge} ${badgeClass}`}>
                                {item.isNew ? 'New' : (item.status || 'Open')}
                            </span>
                        </AccordionSummary>
                        <AccordionDetails className={classes.details}>
                            <ProjectEditor
                                ref={(node) => {
                                    if (node) editorRefs.current[item.key] = node;
                                    else delete editorRefs.current[item.key];
                                }}
                                projectUuid={item.uuid}
                                section={section}
                                active={isOpen}
                                customerId={customerId}
                                customerList={customerList}
                                customerAdminList={customerAdminList}
                                projectAdminList={projectAdminList}
                                onProjectAdminCreated={onProjectAdminCreated}
                                programmeOptions={programmeOptions}
                            />
                            {((item.isNew && onDiscard) || onSave) && (
                                <div className={classes.actionsRow}>
                                    {item.isNew && onDiscard && (
                                        <Button variant="outlined" color="default" onClick={() => onDiscard(item.key)} disabled={savingKey === item.key}>
                                            Discard project
                                        </Button>
                                    )}
                                    {onSave && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            className={classes.saveBtn}
                                            onClick={() => onSave(item.key)}
                                            disabled={!!savingKey}
                                        >
                                            {savingKey === item.key ? <CircularProgress size={18} color="inherit" /> : item.isNew ? 'Create project' : 'Save project'}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </div>
    );
};

ProjectAccordionList.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        uuid: PropTypes.string,
        title: PropTypes.string,
        meta: PropTypes.string,
        status: PropTypes.string,
        isNew: PropTypes.bool,
        programmeLabel: PropTypes.string,
    })).isRequired,
    section: PropTypes.oneOf(['details', 'licences']).isRequired,
    expandedKey: PropTypes.string,
    onExpand: PropTypes.func.isRequired,
    editorRefs: PropTypes.object.isRequired,
    onDiscard: PropTypes.func,
    customerId: PropTypes.string,
    customerList: PropTypes.array,
    customerAdminList: PropTypes.array,
    projectAdminList: PropTypes.array,
    onProjectAdminCreated: PropTypes.func,
    programmeOptions: PropTypes.object,
    onSave: PropTypes.func,
    savingKey: PropTypes.string,
};

/**
 * Saves every edited project in list order and stops at the first failure.
 * Returns the uuid of the last project saved, so later steps know which project to use.
 */
export const saveEditedProjects = async (items, editorRefs) => {
    let lastSavedUuid = '';
    const savedKeys = [];
    for (const item of items) {
        const editor = editorRefs.current[item.key];
        if (!editor) continue;
        const result = await editor.save();
        if (!result.ok) {
            return { ok: false, failedKey: item.key, lastSavedUuid, savedKeys, savedCount: savedKeys.length };
        }
        if (result.saved) {
            savedKeys.push(item.key);
            lastSavedUuid = result.uuid || lastSavedUuid;
        }
    }
    return { ok: true, lastSavedUuid, savedKeys, savedCount: savedKeys.length };
};

/**
 * The Users, Assignment and Review steps read the working project from localStorage.
 */
export const rememberWorkingProject = (lastSavedUuid, projectUuids) => {
    const current = localStorage.getItem('project_id');
    const nextProjectId = lastSavedUuid
        || (current && projectUuids.includes(current) ? current : projectUuids[0])
        || '';
    localStorage.setItem('project_id', nextProjectId);
};

export default ProjectAccordionList;
